import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import WebhookURLDisplay from '../../components/integrations/WebhookURLDisplay';
import SamplePayloadViewer from '../../components/integrations/SamplePayloadViewer';
import FieldMappingEditor from '../../components/integrations/FieldMappingEditor';
import { integrationsApi } from '../../services/integrations-api';
import type { SaveWebhookMappingRequest } from '../../services/integrations-api';
import { handleApiError } from '../../services/api';
import { useConnectTargetOrg } from '../../hooks/useConnectTargetOrg';

type SetupStep = 'create' | 'sample' | 'mapping' | 'complete';

const WebhookSetup: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // This page is reached by a hard navigation from the integrations grid, so the
  // chosen business arrives in the URL rather than in React state. A
  // single-business workspace auto-fills instead (R9) — there is no ambient
  // business to fall back on.
  const [searchParams] = useSearchParams();
  const { targetOrgId: autoOrgId } = useConnectTargetOrg();
  const targetOrgId = searchParams.get('orgId') || autoOrgId || '';
  const [currentStep, setCurrentStep] = useState<SetupStep>('create');
  const [dataSourceId, setDataSourceId] = useState<string>('');
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [webhookName, setWebhookName] = useState<string>('');
  const [samplePayload, setSamplePayload] = useState<Record<string, unknown> | null>(null);

  // Create webhook mutation
  const createWebhookMutation = useMutation({
    mutationFn: async (name: string) => {
      return await integrationsApi.createWebhookDataSource(name, targetOrgId);
    },
    onSuccess: (data) => {
      setDataSourceId(data.id);
      setWebhookUrl(data.webhookUrl);
      setCurrentStep('sample');
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      void queryClient.invalidateQueries({ queryKey: ['connectedIntegrations'] });
      toast.success('Webhook created successfully!');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to create webhook');
    }
  });

  // Check for sample mutation
  const checkSampleMutation = useMutation({
    mutationFn: async () => {
      return await integrationsApi.getWebhookSample(dataSourceId);
    },
    onSuccess: (data) => {
      if (data.hasSample) {
        setSamplePayload((data.samplePayload ?? null) as Record<string, unknown> | null);
        setCurrentStep('mapping');
        toast.success('Sample payload captured!');
      } else {
        toast.error('No sample received yet. Please send a test webhook.');
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to check for sample');
    }
  });

  // Save mapping mutation
  const saveMappingMutation = useMutation({
    mutationFn: async (mapping: SaveWebhookMappingRequest) => {
      return await integrationsApi.saveWebhookMapping(dataSourceId, mapping);
    },
    onSuccess: () => {
      setCurrentStep('complete');
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      toast.success('Webhook integration activated!');
      setTimeout(() => navigate('/sources/integrations'), 2000);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to save mapping');
    }
  });

  const handleCreate = () => {
    if (!webhookName.trim()) {
      toast.error('Please enter a name for the webhook');
      return;
    }
    createWebhookMutation.mutate(webhookName);
  };

  const handleCheckSample = () => {
    checkSampleMutation.mutate();
  };

  const handleSaveMapping = (mapping: SaveWebhookMappingRequest) => {
    saveMappingMutation.mutate(mapping);
  };

  const getStepIndex = (step: SetupStep): number => {
    return ['create', 'sample', 'mapping', 'complete'].indexOf(step);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/sources/integrations')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Integrations
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Setup Custom Webhook Integration
          </h1>
          <p className="text-muted-foreground">
            Connect any webhook-enabled service to Zamino in a few simple steps
          </p>
        </div>
        
        {/* Progress indicator */}
        <div className="mb-8 bg-card rounded-lg shadow p-6">
          <div className="flex justify-between">
            {[
              { step: 'create', label: 'Create' },
              { step: 'sample', label: 'Sample' },
              { step: 'mapping', label: 'Mapping' },
              { step: 'complete', label: 'Complete' }
            ].map((item, i) => {
              const isActive = getStepIndex(currentStep) >= i;
              const isCurrent = currentStep === item.step;
              
              return (
                <div key={item.step} className="flex-1">
                  <div className="relative">
                    {/* Line connector */}
                    {i > 0 && (
                      <div 
                        className={`absolute top-3 -left-1/2 w-full h-0.5 ${
                          isActive 
                            ? 'bg-primary' 
                            : 'bg-muted'
                        }`} 
                        style={{ zIndex: 0 }}
                      />
                    )}
                    
                    {/* Step circle */}
                    <div className="relative flex flex-col items-center" style={{ zIndex: 1 }}>
                      <div 
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        } ${
                          isCurrent ? 'ring-4 ring-primary/30' : ''
                        }`}
                      >
                        {i + 1}
                      </div>
                      <span 
                        className={`text-sm mt-2 font-medium ${
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {item.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="bg-card rounded-lg shadow p-6">
          {currentStep === 'create' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Step 1: Create Webhook Data Source
                </h2>
                <p className="text-muted-foreground">
                  Give your webhook integration a descriptive name to identify it later.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Webhook Name
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input 
                  type="text" 
                  placeholder="e.g., Stripe Payments, Shopify Orders"
                  value={webhookName}
                  onChange={(e) => setWebhookName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreate();
                    }
                  }}
                />
                <p className="mt-1 text-sm text-muted-foreground">
                  Choose a name that describes the source of the data
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={handleCreate}
                  disabled={!webhookName.trim() || createWebhookMutation.isPending}
                  isLoading={createWebhookMutation.isPending}
                >
                  Create & Get Webhook URL
                </Button>
              </div>
            </div>
          )}

          {currentStep === 'sample' && (
            <WebhookURLDisplay 
              webhookUrl={webhookUrl}
              onCheckSample={handleCheckSample}
            />
          )}

          {currentStep === 'mapping' && samplePayload && (
            <div className="space-y-6">
              <SamplePayloadViewer payload={samplePayload} />
              <FieldMappingEditor 
                sample={samplePayload}
                dataSourceId={dataSourceId}
                onSave={handleSaveMapping}
                isLoading={saveMappingMutation.isPending}
              />
            </div>
          )}

          {currentStep === 'complete' && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/15 rounded-full mb-4">
                <svg 
                  className="w-8 h-8 text-success" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-success mb-4">
                Setup Complete!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your webhook integration is now active and ready to receive data.
              </p>
              <p className="text-sm text-muted-foreground">
                Redirecting to integrations page...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebhookSetup;

